import type { ColumnConfig } from '@/components/tables/enhanced-data-table'

import type { AdminApplicationRow } from '../types'
import { ApplicationStatusBadge } from './ApplicationStatusBadge'
import { formatCurrencyUGX, formatDate } from '../utils'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

export function createColumns(): ColumnConfig<AdminApplicationRow>[] {
  return [
    {
      key: 'name',
      header: 'Applicant',
      sortable: true,
      filterable: true,
      accessor: (row) => row.name,
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      filterable: true,
      accessor: (row) => row.email,
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: (row) => row.phone,
      filterable: true,
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.category,
      sortable: true,
      filterable: true,
      cell: (value) => (
        <Badge variant="outline" title={value === 'Student' ? 'For UIBFS enrolled active students' : undefined}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      sortable: true,
      filterable: true,
      cell: (value) => <ApplicationStatusBadge status={value as AdminApplicationRow['status']} />,
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      accessor: (row) => row.submittedAt,
      sortable: true,
      cell: (value) => formatDate(value as string | null) ?? '—',
    },
  ]
}

export function createApprovedColumns(): ColumnConfig<AdminApplicationRow>[] {
  return [
    {
      key: 'name',
      header: 'Member',
      sortable: true,
      filterable: true,
      accessor: (row) => row.name,
    },
    {
      key: 'membershipNumber',
      header: 'Member #',
      accessor: (row) => row.membershipNumber ?? '—',
      filterable: true,
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      filterable: true,
      accessor: (row) => row.email,
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: (row) => row.phone,
      filterable: true,
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.category,
      sortable: true,
      filterable: true,
      cell: (value) => (
        <Badge variant="outline" title={value === 'Student' ? 'For UIBFS enrolled active students' : undefined}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      sortable: true,
      filterable: true,
      cell: (value) => <ApplicationStatusBadge status={value as AdminApplicationRow['status']} />,
    },
    {
      key: 'membershipDuration',
      header: 'Period',
      accessor: (row) => row.membershipDuration ?? '—',
      sortable: true,
    },
    {
      key: 'membershipStartAt',
      header: 'Start',
      accessor: (row) => row.membershipStartAt ?? null,
      sortable: true,
      cell: (value) => formatDate(value as string | null) ?? '—',
    },
    {
      key: 'membershipExpiryAt',
      header: 'Expires',
      accessor: (row) => row.membershipExpiryAt ?? null,
      sortable: true,
      cell: (value) => formatDate(value as string | null) ?? '—',
    },
    {
      key: 'remainingDuration',
      header: 'Remaining',
      accessor: (row) => row.remainingDuration ?? '—',
      cell: (value, row) => {
        const days = (row as AdminApplicationRow).remainingDays
        const label = (value as string) ?? '—'
        if (days === null || days === undefined || label === '—') return label

        const isOverdue = days < 0
        const isExpiringSoon = days > 0 && days <= 30
        
        let toneClasses = 'border-primary/40 bg-primary/10 text-primary-700 dark:text-primary-400'
        let displayLabel = label
        
        if (isOverdue) {
          toneClasses = 'border-destructive bg-destructive text-destructive-foreground'
          displayLabel = 'Overdue'
        } else if (isExpiringSoon) {
          toneClasses = 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400'
        }
        
        return (
          <Badge
            variant={isOverdue ? 'destructive' : 'outline'}
            className={cn(
              'font-medium',
              !isOverdue && toneClasses,
            )}
            title={isOverdue ? `Expired ${Math.abs(days)} days ago` : `${days} days remaining`}
          >
            {displayLabel}
          </Badge>
        )
      },
    },
    {
      key: 'membershipAmount',
      header: 'Amount (UGX)',
      accessor: (row) => row.membershipAmount ?? null,
      sortable: true,
      cell: (value) => formatCurrencyUGX(value as number | null),
    },
  ]
}
